import { useCallback } from 'react';
import { useCumulativeSearchParams } from '../../../hooks/cumulativeSearchParams';
import { useFirstPathSegment } from '../../../hooks/useFirstPathSegment';
import { useStorage } from '../../../hooks/useStorage';
import { useCurrentUser } from '../../../hooks';
import { PageSize } from './types';

export type UsePaginationSearchParamsResult = {
  pageSize: PageSize;
  pageIndex: number;
  setPageSize: (size: PageSize) => void;
  setPageIndex: (index: number) => void;
};

/**
 * Handles pagination state variables as search params.
 * Will reset the page index to 0 when the page size changes.
 *
 * Despite the naming of the function arguments, this hook can
 * be used for any pagination method (e.g. limit/offset).
 */
export const usePaginationStorage = (
  pageSizeKey = 'pageSize',
  pageIndexKey = 'pageIndex',
): UsePaginationSearchParamsResult => {
  const [searchParams, setSearchParams] = useCumulativeSearchParams();
  const [locallyStoredPageSize, setLocallyStoredPageSize] =
    useLocallyStoredPageSize(pageSizeKey);

  const pageSize =
    (parseInt(
      searchParams.get(pageSizeKey) ?? `${locallyStoredPageSize}`,
    ) as PageSize) || 15;
  const pageIndex = parseInt(searchParams.get(pageIndexKey) ?? '0');

  const setPageSize = useCallback(
    (size: PageSize) => {
      setSearchParams({ [pageSizeKey]: size.toString(), [pageIndexKey]: '0' });
      setLocallyStoredPageSize(size);
    },
    [pageSizeKey, pageIndexKey, setSearchParams],
  );
  const setPageIndex = useCallback(
    (index: number) => {
      setSearchParams({ [pageIndexKey]: index.toString() });
    },
    [pageIndexKey, setSearchParams],
  );
  return {
    pageSize,
    pageIndex,
    setPageSize,
    setPageIndex,
  };
};

const useLocallyStoredPageSize = (pageSizeKey: string) => {
  const key = usePersistenceKey(pageSizeKey);
  return useStorage(localStorage, key);
};

const usePersistenceKey = (pageSizeKey: string): string => {
  const { companyId: instanceId } = useCurrentUser();
  return `${instanceId}~PAGINATION~${useFirstPathSegment()}§${pageSizeKey}`;
};
